import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { SproutMedia } from "../../../../modules/content/components/SproutMedia/SproutMedia";
import { SproutRichText } from "../../../../modules/content/components/SproutRichText/SproutRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center font-sprout-mono text-xs text-mineral-gray/80 transition-colors hover:text-mineral-gray focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-oxidized-orange";
  return (
    <footer
      className={`${parentClass || ""} border-t-[0.75rem] border-coral bg-charcoal-green-ink text-mineral-gray`}
    >
      <div className="sprout-container py-16 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_2fr_0.8fr]">
          {" "}
          <div>
            {logo ? (
              <SproutMedia
                className="mb-7 h-12 w-52 -rotate-1 rounded-lg bg-whiteboard p-3"
                imageClassName="object-contain object-left"
                media={logo}
                sizes="192px"
              />
            ) : null}
            {description ? (
              <SproutRichText
                className="max-w-sm text-base text-mineral-gray/80 md:text-lg"
                content={description}
              />
            ) : null}
            {contact ? (
              <address className="mt-5 flex flex-col not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 font-sprout-mono text-xs leading-6 text-mineral-gray/80">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav aria-label="Footer" className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="mb-3 w-fit rounded-md bg-sunflower px-3 py-1.5 font-sprout-display text-sm font-bold text-navy-ink">
                    {group.header}
                  </h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-wrap content-start gap-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border-2 border-sunflower bg-sunflower px-4 font-sprout-mono text-xs font-extrabold text-navy-ink hover:bg-whiteboard focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sunflower"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.icon ? (
                    <Icon aria-hidden="true" className="size-4" name={item.icon} />
                  ) : null}
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t border-mineral-gray/35 pt-6 font-sprout-mono text-xs text-mineral-gray/70 sm:flex-row sm:items-center sm:justify-between">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
